import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useCreateQualityRule, useUpdateQualityRule } from '@/hooks/useQuality';
import { useToast } from '@/hooks/useToast';
import type { QualityRule, QualityRuleType } from '@/types';

interface QualityRuleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: QualityRule | null;
}

export const QualityRuleModal: React.FC<QualityRuleModalProps> = ({
  open,
  onOpenChange,
  rule,
}) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<QualityRuleType>('completeness');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState('{}');

  const createRule = useCreateQualityRule();
  const updateRule = useUpdateQualityRule();
  const { toast } = useToast();

  const isEditMode = !!rule;

  // Reset the form when the dialog opens/closes or the edited rule changes.
  // Adjusted during render (React's documented pattern for resetting state
  // from props: https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // instead of in an effect: `hasSeeded`/`seededRule`/`seededOpen` mirror what
  // the form was last seeded from. A mismatch — including the first render —
  // seeds the form synchronously and updates the mirrors, so React re-renders
  // once more before committing and the branch converges (no further updates).
  const [hasSeeded, setHasSeeded] = useState(false);
  const [seededRule, setSeededRule] = useState(rule);
  const [seededOpen, setSeededOpen] = useState(open);
  if (!hasSeeded || rule !== seededRule || open !== seededOpen) {
    setHasSeeded(true);
    setSeededRule(rule);
    setSeededOpen(open);
    if (rule) {
      setName(rule.name || '');
      setType(rule.type || 'completeness');
      setDescription(rule.description || '');
      setEnabled(rule.enabled !== undefined ? rule.enabled : true);
      setConfig(JSON.stringify(rule.config || {}, null, 2));
    } else {
      // Reset form for create mode
      setName('');
      setType('completeness');
      setDescription('');
      setEnabled(true);
      setConfig('{}');
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate config JSON
    let parsedConfig = {};
    try {
      parsedConfig = JSON.parse(config);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Invalid JSON configuration',
        variant: 'destructive',
      });
      return;
    }

    const ruleData = {
      name,
      type,
      description,
      config: parsedConfig,
      enabled,
    };

    try {
      if (isEditMode && rule) {
        await updateRule.mutateAsync({ id: rule.id, ...ruleData });
        toast({
          title: 'Success',
          description: 'Quality rule updated successfully',
        });
      } else {
        await createRule.mutateAsync(ruleData);
        toast({
          title: 'Success',
          description: 'Quality rule created successfully',
        });
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} quality rule`,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Quality Rule' : 'Create Quality Rule'}</DialogTitle>
            <DialogDescription>
              {isEditMode
                ? 'Update the quality rule configuration'
                : 'Define a new data quality validation rule'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Check null values"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="type">Type *</Label>
              <Select value={type} onValueChange={(value) => setType(value as QualityRuleType)}>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select rule type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completeness">Completeness</SelectItem>
                  <SelectItem value="uniqueness">Uniqueness</SelectItem>
                  <SelectItem value="validity">Validity</SelectItem>
                  <SelectItem value="consistency">Consistency</SelectItem>
                  <SelectItem value="accuracy">Accuracy</SelectItem>
                  <SelectItem value="timeliness">Timeliness</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this rule validates..."
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="config">Configuration (JSON)</Label>
              <Textarea
                id="config"
                value={config}
                onChange={(e) => setConfig(e.target.value)}
                placeholder='{"column": "email", "pattern": ".*@.*"}'
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Provide rule-specific configuration as valid JSON
              </p>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="enabled">Enable this rule</Label>
              <Switch
                id="enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createRule.isPending || updateRule.isPending}
            >
              {createRule.isPending || updateRule.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update Rule'
                : 'Create Rule'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
